/**
 * Sidebar v2 drop planning: what a drag drop means for a thread's section,
 * lifecycle flags, and order keys.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.logic.ts`
 * (planSidebarThreadDrop / applySidebarThreadDrop / SidebarThreadDropPlan)
 * at pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: key math comes from this port's
 * sidebarV2Sort module; the projected fields are applied to forge's
 * client-side sidebar overlay (see sidebarV2Threads) because forge threads
 * persist settle server-side and pin/order/snooze client-side, unlike
 * upstream's server-side thread fields.
 */
import type { SidebarSection } from "./sidebarV2List";
import { planPinnedReorder } from "./sidebarV2Sort";

export type SidebarThreadDropPlan =
  | { readonly kind: "none" }
  /** Within the pinned block: the existing key writes. */
  | {
      readonly kind: "reorder-pinned";
      readonly order: readonly string[];
      readonly assignments: ReadonlyArray<{ readonly id: string; readonly orderKey: string }>;
    }
  /** From another section into the pinned block. Fresh pins take `orderKey`
      on the pin command. `extraAssignments` land afterward, including the
      moved row when it was already pinned beneath a snooze. */
  | {
      readonly kind: "pin";
      readonly order: readonly string[];
      readonly orderKey: string | undefined;
      readonly extraAssignments: ReadonlyArray<{ readonly id: string; readonly orderKey: string }>;
    }
  | {
      readonly kind: "move-active";
      readonly order: readonly string[];
      readonly assignments: ReadonlyArray<{ readonly id: string; readonly orderKey: string }>;
      readonly unpin: boolean;
      readonly unsettle: boolean;
      readonly unsnooze: boolean;
    }
  | { readonly kind: "settle" };

interface DropPlanContext {
  readonly activeKey: string;
  readonly activeSection: SidebarSection;
  readonly activePinned: boolean;
  readonly activeSettled: boolean;
  readonly supportsSettlement: boolean;
  readonly target: {
    readonly section: "pinned" | "active" | "settled";
    readonly pinnedOrder: readonly string[];
    readonly activeOrder: readonly string[];
  };
  readonly pinnedOrder: readonly string[];
  readonly pinnedKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly reorderableKeys?: ReadonlySet<string> | undefined;
  readonly activeOrder: readonly string[];
  readonly activeKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly activeReorderableKeys?: ReadonlySet<string> | undefined;
}

/** A drop whose destination is the active inbox: wake/re-anchor the moved
 * thread at the chosen slot, clearing whatever parked it before. */
function planActiveSectionDrop(context: DropPlanContext): SidebarThreadDropPlan {
  const order = context.target.activeOrder;
  if (
    context.activeSection === "active" &&
    order.length === context.activeOrder.length &&
    order.every((key, index) => key === context.activeOrder[index])
  ) {
    return { kind: "none" };
  }
  const assignments = planPinnedReorder({
    orderedIds: order,
    keysById: context.activeKeysById,
    movedId: context.activeKey,
  });
  const activeReorderable = context.activeReorderableKeys;
  if (activeReorderable && assignments.some(({ id }) => !activeReorderable.has(id))) {
    return { kind: "none" };
  }
  return {
    kind: "move-active",
    order,
    assignments,
    unpin: context.activePinned,
    unsettle: context.activeSettled,
    unsnooze: context.activeSection === "snoozed",
  };
}

/** A drop onto the settled shelf: settle anything not already settled. */
function planSettledSectionDrop(context: DropPlanContext): SidebarThreadDropPlan {
  return context.activeSection === "settled" ? { kind: "none" } : { kind: "settle" };
}

/** A drop into the pinned block: pin with a fresh key when coming from
 * another section, or rewrite the run when a neighbor is keyless. */
function planPinnedSectionDrop(context: DropPlanContext): SidebarThreadDropPlan {
  const order = context.target.pinnedOrder;
  if (
    context.activeSection === "pinned" &&
    order.length === context.pinnedOrder.length &&
    order.every((key, index) => key === context.pinnedOrder[index])
  ) {
    return { kind: "none" };
  }
  const assignments = planPinnedReorder({
    orderedIds: order,
    keysById: context.pinnedKeysById,
    movedId: context.activeKey,
  });
  const reorderable = context.reorderableKeys;
  if (reorderable && assignments.some(({ id }) => !reorderable.has(id))) {
    return { kind: "none" };
  }
  return context.activeSection === "pinned"
    ? finishPinnedReorder(context.activeSection, order, assignments)
    : finishFreshPin(
        context.activeSection,
        context.activeKey,
        context.activePinned,
        order,
        assignments,
      );
}

/** Return the pinned reorder writes, or a no-op plan when no order keys need changing. */
function finishPinnedReorder(
  activeSection: SidebarSection,
  order: readonly string[],
  assignments: ReadonlyArray<{ readonly id: string; readonly orderKey: string }>,
): SidebarThreadDropPlan {
  return assignments.length === 0
    ? { kind: "none" }
    : { kind: "reorder-pinned", order, assignments };
}

/** Build a pin plan with the moved row key and any additional key assignments required by the reorder. */
function finishFreshPin(
  activeSection: SidebarSection,
  activeKey: string,
  activePinned: boolean,
  order: readonly string[],
  assignments: ReadonlyArray<{ readonly id: string; readonly orderKey: string }>,
): SidebarThreadDropPlan {
  return {
    kind: "pin",
    order,
    orderKey: assignments.find((assignment) => assignment.id === activeKey)?.orderKey,
    extraAssignments: activePinned
      ? assignments
      : assignments.filter((assignment) => assignment.id !== activeKey),
  };
}

/** Plan a drop from the lifted thread's section into the hovered section. */
export function planSidebarThreadDrop(input: {
  readonly activeKey: string;
  readonly activeSection: SidebarSection;
  /** Snoozed threads can retain pinning and settlement beneath the shelf. */
  readonly activePinned?: boolean;
  readonly activeSettled?: boolean;
  readonly supportsSettlement?: boolean;
  readonly target: {
    readonly section: "pinned" | "active" | "settled";
    readonly pinnedOrder: readonly string[];
    readonly activeOrder: readonly string[];
  };
  /** All pinned keys in displayed order before the drop. */
  readonly pinnedOrder: readonly string[];
  readonly pinnedKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly reorderableKeys?: ReadonlySet<string>;
  readonly activeOrder: readonly string[];
  readonly activeKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly activeReorderableKeys?: ReadonlySet<string>;
}): SidebarThreadDropPlan {
  const context: DropPlanContext = {
    activeKey: input.activeKey,
    activeSection: input.activeSection,
    activePinned: input.activePinned ?? input.activeSection === "pinned",
    activeSettled: input.activeSettled ?? input.activeSection === "settled",
    supportsSettlement: input.supportsSettlement ?? true,
    target: input.target,
    pinnedOrder: input.pinnedOrder,
    pinnedKeysById: input.pinnedKeysById,
    reorderableKeys: input.reorderableKeys,
    activeOrder: input.activeOrder,
    activeKeysById: input.activeKeysById,
    activeReorderableKeys: input.activeReorderableKeys,
  };
  if (
    context.supportsSettlement === false &&
    (context.target.section === "settled" || context.activeSettled)
  ) {
    return { kind: "none" };
  }
  switch (context.target.section) {
    case "active":
      return planActiveSectionDrop(context);
    case "settled":
      return planSettledSectionDrop(context);
    case "pinned":
      return planPinnedSectionDrop(context);
  }
}

/** The lifecycle fields a drop projects. Forge keeps settle server-side;
    pin, order keys, and snooze are the sidebar's client-side overlay, so
    the projected record is the overlay's shape rather than a wire type. */
export type SidebarDropProjection = {
  readonly pinnedAt: string | null;
  readonly pinOrderKey: string | null;
  readonly activeOrderKey: string | null;
  readonly snoozedAt: string | null;
  readonly snoozedUntil: string | null;
  readonly settledOverride: "settled" | "active" | null;
  readonly settledAt: string | null;
  readonly unsettledAt: string | null;
};

/** Project a drop's lifecycle fields before sorting its destination. Reusing
    the server's re-entry rules keeps the preview in place when state
    arrives. Generic so callers carrying identity fields (id, createdAt)
    keep them for sorting. */
export function applySidebarThreadDrop<T extends SidebarDropProjection>(
  thread: T,
  section: "pinned" | "active" | "settled",
  now: string,
  orderKey?: string,
): T {
  const wasSettled = thread.settledOverride === "settled";
  const awake = { ...thread, snoozedAt: null, snoozedUntil: null };
  if (section === "settled") {
    return {
      ...awake,
      pinnedAt: null,
      pinOrderKey: null,
      activeOrderKey: null,
      settledOverride: "settled",
      settledAt: wasSettled ? (thread.settledAt ?? now) : now,
      unsettledAt: null,
    };
  }
  const resumed = wasSettled
    ? { ...awake, settledOverride: "active" as const, settledAt: null, unsettledAt: now }
    : awake;
  return {
    ...resumed,
    pinnedAt: section === "pinned" ? (thread.pinnedAt ?? now) : null,
    pinOrderKey: section === "pinned" ? (orderKey ?? thread.pinOrderKey) : null,
    ...(section === "active" && orderKey !== undefined ? { activeOrderKey: orderKey } : {}),
  };
}
